import * as React from 'react';
import {
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import EmailIcon from '@mui/icons-material/Email';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import InfoIcon from '@mui/icons-material/Info';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useIsAdmin } from '../lib/auth/useIsAdmin';
import { performSignOut } from '../lib/auth/sign-out';

interface NavEntry {
  readonly label: string;
  readonly to: string;
  readonly icon: React.ReactNode;
}

const PRIMARY_ENTRIES: readonly NavEntry[] = [
  { label: 'Daily', to: '/', icon: <HomeIcon /> },
  { label: 'Days', to: '/days', icon: <CalendarTodayIcon /> },
  { label: 'Games', to: '/games', icon: <SportsEsportsIcon /> },
  { label: 'Newsletter', to: '/newsletter', icon: <EmailIcon /> },
  { label: 'Podcast', to: '/podcast', icon: <PodcastsIcon /> },
  { label: 'About', to: '/about', icon: <InfoIcon /> },
];

/**
 * Side-navigation drawer body per Requirement 2.1.
 *
 * Renders the six primary entries (`Daily`, `Days`, `Games`,
 * `Newsletter`, `Podcast`, `About`) in the listed order, followed by
 * a {@link Divider} and either:
 *   - `Sign in` link (unauthenticated), OR
 *   - `Profile` + `Sign out` (authenticated).
 *
 * Admin visitors — recognized via the ID token's
 * `cognito:groups` claim per round-22 Phase D
 * (`useIsAdmin()`) — see one additional entry: `Admin` linking
 * to `/admin`. The entry never renders for non-admin visitors,
 * so it's discoverable only when the capability is actually
 * granted. There is no user-facing "become an admin" toggle;
 * admin membership is granted out-of-band by the operator via
 * `grant-admin.sh`.
 *
 * _Requirements: Requirement 2.1, R8.4, design §3.2._
 */
export function SideNav(): JSX.Element {
  const isAuthenticated = useAppSelector(
    (state) => state.auth.status === 'authenticated'
  );
  const isAdmin = useIsAdmin();
  useAppDispatch(); // reserved for future admin action wiring
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return (
    <List>
      {PRIMARY_ENTRIES.map((entry) => (
        <ListItemButton key={entry.to} href={entry.to}>
          <ListItemIcon>{entry.icon}</ListItemIcon>
          <ListItemText primary={entry.label} />
        </ListItemButton>
      ))}
      <Divider />
      {!isAuthenticated && (
        <ListItemButton href="/sign-in">
          <ListItemIcon>
            <LoginIcon />
          </ListItemIcon>
          <ListItemText primary="Sign in" />
        </ListItemButton>
      )}
      {isAuthenticated && (
        <>
          <ListItemButton href="/profile">
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItemButton>
          {isAdmin && (
            <ListItemButton href="/admin">
              <ListItemIcon>
                <AdminPanelSettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Admin" />
            </ListItemButton>
          )}
          <ListItemButton
            onClick={() => {
              performSignOut({ dispatch, navigate });
            }}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </>
      )}
    </List>
  );
}
